import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Descriptions, Form, Modal, Radio, Select, Spin } from 'antd';

import Input from '../../../components/Input/Input';
import Notification, { NOTIFICATION_TYPE } from '../../../components/notification/Notification';
import handleError from '../../../utils/handleError';
import { formatDateUtc } from '../../../utils/moment.functions';
import { getSalesSFDiagnosticCustomer, updateSalesSFDiagnosticCustomer } from '../../../utils/webApi';
import { isValidCEP, isValidCNPJ, isValidCPF, isValidEmail, onlyDigits } from '../../../utils/document.validators';
import {
    CustomerType,
    IDiagnosticAddress,
    IDiagnosticCustomer,
    IDiagnosticItem,
} from '../../../models/sales-sf-diagnostic.models';
import { CategoryTag, ReasonDetail, SituationTag, formatToBRL } from '../diagnostic.helpers';

import './CustomerEdit.scss';

const { Option } = Select;

/**
 * Lista fechada de tipos de logradouro aceita pelo gateway fiscal
 * (febracis-invoices `src/api/invoices/model.js` — `streetType`; mesma lista do
 * scis-pagamento-frontend `invoiceInforConstants.js` e do enum `InvoiceTypeAddress`
 * do febracis-coaches, conforme docs/febracis-coaches/03-dominio.md).
 */
const TYPE_ADDRESS_OPTIONS: { label: string, value: string }[] = [
    { label: 'Alameda', value: 'ALAMEDA' },
    { label: 'Avenida', value: 'AVENIDA' },
    { label: 'Chácara', value: 'CHÁCARA' },
    { label: 'Colônia', value: 'COLÔNIA' },
    { label: 'Condomínio', value: 'CONDOMÍNIO' },
    { label: 'Estância', value: 'ESTÂNCIA' },
    { label: 'Estrada', value: 'ESTRADA' },
    { label: 'Fazenda', value: 'FAZENDA' },
    { label: 'Praça', value: 'PRAÇA' },
    { label: 'Prolongamento', value: 'PROLONGAMENTO' },
    { label: 'Rodovia', value: 'RODOVIA' },
    { label: 'Rua', value: 'RUA' },
    { label: 'Sítio', value: 'SÍTIO' },
    { label: 'Travessa', value: 'TRAVESSA' },
    { label: 'Vicinal', value: 'VICINAL' },
    { label: 'EQNP', value: 'EQNP' },
];

const UFS = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
    'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const ORIGIN_COUNTRY_MAX = 20;

type FieldErrors = { [field: string]: string };

const str = (value: any): string => (value === null || value === undefined ? '' : String(value));

const normalizeCustomer = (raw: Partial<IDiagnosticCustomer> | null | undefined): IDiagnosticCustomer => {
    const c: any = raw || {};
    const doc: any = c.document || {};
    const addr: any = c.address || {};

    return {
        type: c.type || doc.type || null,
        firstName: str(c.firstName),
        lastName: str(c.lastName),
        corporateName: str(c.corporateName),
        doingBusiness: str(c.doingBusiness),
        email: str(c.email),
        phone: str(c.phone),
        originCountry: str(c.originCountry),
        document: { type: doc.type || c.type || null, number: str(doc.number) },
        address: {
            typeAddress: str(addr.typeAddress).toUpperCase(),
            street: str(addr.street),
            number: str(addr.number),
            complement: str(addr.complement),
            district: str(addr.district),
            city: str(addr.city),
            state: str(addr.state).toUpperCase(),
            country: str(addr.country),
            postalCode: str(addr.postalCode),
        },
    };
};

/** Quando o backend não informa o tipo, deduz pelo tamanho do documento. */
const inferType = (customer: IDiagnosticCustomer): CustomerType => {
    if (customer.type) return customer.type;
    const digits = onlyDigits(customer.document.number);
    if (digits.length === 14) return 'CNPJ';
    if (customer.originCountry) return 'FOREIGNER';
    return 'CPF';
};

const REQUIRED_MESSAGE = 'Campo obrigatório';

// Campos que caracterizam um endereço "começado" (country/complement ficam de
// fora: sobram de pré-preenchimento e sozinhos não significam endereço informado).
// Mesma regra do backend (febracis-admin sf-diagnostic `missingAddressFields`).
const ADDRESS_CORE_FIELDS: (keyof IDiagnosticAddress)[] = ['typeAddress', 'street', 'number', 'district', 'city', 'state', 'postalCode'];

const isAddressStarted = (address: IDiagnosticAddress): boolean =>
    ADDRESS_CORE_FIELDS.some(field => str(address[field]).trim() !== '');

const validate = (customer: IDiagnosticCustomer, type: CustomerType): FieldErrors => {
    const errors: FieldErrors = {};
    const required = (field: string, value: string) => {
        if (!value.trim()) errors[field] = REQUIRED_MESSAGE;
    };

    if (type === 'CNPJ') {
        required('corporateName', customer.corporateName);
        required('doingBusiness', customer.doingBusiness);
    } else {
        required('firstName', customer.firstName);
        required('lastName', customer.lastName);
    }

    required('email', customer.email);
    if (!errors.email && !isValidEmail(customer.email)) errors.email = 'E-mail inválido';

    required('document.number', customer.document.number);
    if (!errors['document.number']) {
        if (type === 'CPF' && !isValidCPF(customer.document.number)) errors['document.number'] = 'CPF inválido';
        if (type === 'CNPJ' && !isValidCNPJ(customer.document.number)) errors['document.number'] = 'CNPJ inválido';
    }

    if (type === 'FOREIGNER') {
        required('originCountry', customer.originCountry);
        if (customer.originCountry.trim().length > ORIGIN_COUNTRY_MAX) {
            errors.originCountry = `Máximo de ${ORIGIN_COUNTRY_MAX} caracteres`;
        }
        return errors;
    }

    required('phone', customer.phone);

    // Endereço é opcional (mesmo padrão da tela Vendas SF); quando algum campo
    // é preenchido, o bloco inteiro passa a ser exigido — o febracis-coaches
    // rejeita endereço parcial.
    const address = customer.address;
    if (!isAddressStarted(address)) {
        return errors;
    }
    required('address.typeAddress', address.typeAddress);
    if (!errors['address.typeAddress'] && !TYPE_ADDRESS_OPTIONS.some(o => o.value === address.typeAddress)) {
        errors['address.typeAddress'] = 'Selecione um tipo de logradouro válido';
    }
    required('address.street', address.street);
    required('address.number', address.number);
    required('address.district', address.district);
    required('address.city', address.city);
    required('address.state', address.state);
    if (!errors['address.state'] && UFS.indexOf(address.state) === -1) errors['address.state'] = 'UF inválida';
    required('address.country', address.country);
    required('address.postalCode', address.postalCode);
    if (!errors['address.postalCode'] && !isValidCEP(address.postalCode)) errors['address.postalCode'] = 'CEP deve ter 8 dígitos';

    return errors;
};

const trimAll = (customer: IDiagnosticCustomer): IDiagnosticCustomer => ({
    ...customer,
    firstName: customer.firstName.trim(),
    lastName: customer.lastName.trim(),
    corporateName: customer.corporateName.trim(),
    doingBusiness: customer.doingBusiness.trim(),
    email: customer.email.trim(),
    phone: customer.phone.trim(),
    originCountry: customer.originCountry.trim(),
    document: { ...customer.document, number: customer.document.number.trim() },
    address: {
        ...customer.address,
        street: customer.address.street.trim(),
        number: customer.address.number.trim(),
        complement: customer.address.complement.trim(),
        district: customer.address.district.trim(),
        city: customer.address.city.trim(),
        country: customer.address.country.trim(),
    },
});

const buildPayload = (customer: IDiagnosticCustomer, type: CustomerType): IDiagnosticCustomer => {
    const trimmed = trimAll(customer);
    const isBrazilian = type === 'CPF' || type === 'CNPJ';

    return {
        ...trimmed,
        type,
        document: {
            type,
            number: isBrazilian ? onlyDigits(trimmed.document.number) : trimmed.document.number,
        },
        address: {
            ...trimmed.address,
            postalCode: onlyDigits(trimmed.address.postalCode),
        },
    };
};

interface IProps {
    item: IDiagnosticItem,
    onClose: () => void,
    onSaved: () => void,
}

const CustomerEdit = ({ item, onClose, onSaved }: IProps) => {
    const [customer, setCustomer] = useState<IDiagnosticCustomer>();
    const [type, setType] = useState<CustomerType>('CPF');
    const [orderId, setOrderId] = useState<string | null>(item.orderId);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    // Erros só aparecem depois que o campo foi alterado ou após uma tentativa
    // de salvar — um cliente sem invoice-info abre o modal legitimamente vazio.
    const [touched, setTouched] = useState<{ [field: string]: boolean }>({});
    const [submitted, setSubmitted] = useState<boolean>(false);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setLoadError(undefined);

        getSalesSFDiagnosticCustomer(item.userId, item.orderId)
            .then(response => {
                if (!active) return;
                const loaded = normalizeCustomer(response && response.customer);
                setCustomer(loaded);
                setType(inferType(loaded));
                if (response && response.orderId) setOrderId(response.orderId);
            })
            .catch(error => {
                if (!active) return;
                const response = error && error.response;
                if (response && response.status === 401) {
                    handleError(error);
                    return;
                }
                const data = response && response.data;
                setLoadError((data && data.error_description) || (error && error.message) || 'Erro ao carregar os dados do cliente');
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });

        return () => {
            active = false;
        };
    }, [item.userId, item.orderId]);

    const errors = useMemo<FieldErrors>(() => (customer ? validate(customer, type) : {}), [customer, type]);
    const isValid = Object.keys(errors).length === 0;

    const change = (field: string, value: string) => {
        setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }));
        setCustomer(prev => {
            if (!prev) return prev;
            const [root, child] = field.split('.');
            if (child) {
                return { ...prev, [root]: { ...(prev as any)[root], [child]: value } } as IDiagnosticCustomer;
            }
            return { ...prev, [root]: value } as IDiagnosticCustomer;
        });
    };

    const getValue = (field: string): string => {
        if (!customer) return '';
        const [root, child] = field.split('.');
        const value = child ? (customer as any)[root][child] : (customer as any)[root];
        return str(value);
    };

    const showError = (field: string): string | undefined =>
        (submitted || touched[field]) ? errors[field] : undefined;

    const handleSave = async () => {
        if (!customer || isSaving) return;
        if (!isValid) {
            setSubmitted(true);
            Notification(NOTIFICATION_TYPE.WARNING, 'Campos inválidos', 'Corrija os campos destacados antes de salvar.');
            return;
        }
        try {
            setIsSaving(true);
            await updateSalesSFDiagnosticCustomer(item.userId, {
                orderId: orderId || undefined,
                customer: buildPayload(customer, type),
            });
            Notification(NOTIFICATION_TYPE.SUCCESS, 'Sucesso', 'Dados do cliente atualizados com sucesso.');
            onSaved();
        } catch (error) {
            handleError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const renderInput = (field: string, label: string, isRequired: boolean, extra?: { maxLength?: number, placeholder?: string }) => (
        <Form.Item
            label={label}
            required={isRequired}
            validateStatus={showError(field) ? 'error' : undefined}
            help={showError(field)}
        >
            <Input
                value={getValue(field)}
                placeholder={(extra && extra.placeholder) || label}
                maxLength={extra && extra.maxLength}
                onChange={({ target }) => change(field, target.value)}
            />
        </Form.Item>
    );

    const renderSelect = (field: string, label: string, options: { label: string, value: string }[], isRequired: boolean = true) => {
        const value = getValue(field);
        const hasValue = !value || options.some(o => o.value === value);
        return (
            <Form.Item
                label={label}
                required={isRequired}
                validateStatus={showError(field) ? 'error' : undefined}
                help={showError(field)}
            >
                <Select
                    showSearch
                    // allowClear permite voltar ao estado "endereço vazio", que agora é válido
                    allowClear
                    value={value || undefined}
                    placeholder="Selecione"
                    optionFilterProp="children"
                    onChange={(selected: string) => change(field, selected || '')}
                >
                    {!hasValue && <Option key={value} value={value}>{value}</Option>}
                    {options.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
            </Form.Item>
        );
    };

    const isBrazilian = type === 'CPF' || type === 'CNPJ';
    const addressStarted = !!customer && isAddressStarted(customer.address);
    const documentLabel = type === 'CPF' ? 'CPF' : type === 'CNPJ' ? 'CNPJ' : 'Documento';

    return (
        <Modal
            className="modal-sales-sf-diagnostic"
            title="Detalhes da venda"
            visible={true}
            onCancel={onClose}
            destroyOnClose={true}
            maskClosable={false}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    loading={isSaving}
                    disabled={!customer || isSaving || isLoading}
                    onClick={handleSave}
                >
                    Salvar
                </Button>,
            ]}
        >
            <Descriptions size="small" column={2} bordered className="diagnostic-sale-info">
                <Descriptions.Item label="Situação"><SituationTag situation={item.situation} /></Descriptions.Item>
                <Descriptions.Item label="Causa"><CategoryTag category={item.category} /></Descriptions.Item>
                <Descriptions.Item label="Motivo" span={2}><ReasonDetail reason={item.reason} /></Descriptions.Item>
                <Descriptions.Item label="Produto">{item.product || '-'}</Descriptions.Item>
                <Descriptions.Item label="Assinatura">{item.subscription || '-'}</Descriptions.Item>
                <Descriptions.Item label="Meio de pagamento">{item.paymentMethod || '-'}</Descriptions.Item>
                <Descriptions.Item label="Valor">{formatToBRL(item.value)}</Descriptions.Item>
                <Descriptions.Item label="Data de pagamento">
                    {item.paymentDate ? formatDateUtc(item.paymentDate, 'DD/MM/YYYY HH:mm') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="E-mail">{item.email || '-'}</Descriptions.Item>
                <Descriptions.Item label="ID do usuário">{item.userId || '-'}</Descriptions.Item>
                <Descriptions.Item label="ID de pagamento">{item.paymentId || '-'}</Descriptions.Item>
                <Descriptions.Item label="ID de transação">{item.transactionId || '-'}</Descriptions.Item>
                <Descriptions.Item label="ID do pedido">{orderId || '-'}</Descriptions.Item>
            </Descriptions>

            <h3 className="customer-title">Dados do cliente</h3>

            {isLoading && (
                <div className="customer-loading"><Spin /></div>
            )}

            {!isLoading && loadError && (
                <Alert type="error" showIcon message="Não foi possível carregar os dados do cliente" description={loadError} />
            )}

            {!isLoading && customer && (
                <Form layout="vertical" className="customer-form" onSubmit={e => e.preventDefault()}>
                    <Form.Item label="Tipo de pessoa" required>
                        <Radio.Group value={type} onChange={e => setType(e.target.value)}>
                            <Radio.Button value="CPF">CPF</Radio.Button>
                            <Radio.Button value="CNPJ">CNPJ</Radio.Button>
                            <Radio.Button value="FOREIGNER">Estrangeiro</Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <div className="customer-grid">
                        {type === 'CNPJ' ? (
                            <>
                                {renderInput('corporateName', 'Razão social', true)}
                                {renderInput('doingBusiness', 'Nome fantasia', true)}
                            </>
                        ) : (
                            <>
                                {renderInput('firstName', 'Nome', true)}
                                {renderInput('lastName', 'Sobrenome', true)}
                            </>
                        )}

                        {renderInput('document.number', documentLabel, true)}
                        {type === 'FOREIGNER' && renderInput('originCountry', 'País de origem', true, { maxLength: ORIGIN_COUNTRY_MAX })}
                        {renderInput('email', 'E-mail', true)}
                        {renderInput('phone', 'Telefone', isBrazilian)}
                    </div>

                    {isBrazilian && (
                        <>
                            <h4 className="customer-subtitle">Endereço (opcional — ao preencher um campo, preencha todos)</h4>
                            <div className="customer-grid">
                                {renderSelect('address.typeAddress', 'Tipo de logradouro', TYPE_ADDRESS_OPTIONS, addressStarted)}
                                {renderInput('address.street', 'Rua', addressStarted)}
                                {renderInput('address.number', 'Número', addressStarted)}
                                {renderInput('address.complement', 'Complemento', false)}
                                {renderInput('address.district', 'Bairro', addressStarted)}
                                {renderInput('address.city', 'Cidade', addressStarted)}
                                {renderSelect('address.state', 'UF', UFS.map(uf => ({ label: uf, value: uf })), addressStarted)}
                                {renderInput('address.country', 'País', addressStarted)}
                                {renderInput('address.postalCode', 'CEP', addressStarted, { placeholder: '00000-000' })}
                            </div>
                        </>
                    )}
                </Form>
            )}
        </Modal>
    );
};

export default CustomerEdit;
